import React, { MouseEvent, useState } from "react";

import { Menu } from "@mui/material";
import MUIMenuItem from "@mui/material/MenuItem";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import { DeleteIconButton, IconButton } from "veneer/IconButton";
import SvgIcon from "veneer/SvgIcon";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

export interface SavedFilterSet<F> {
  filters: F;
  name: string;
}

interface SavedFiltersProps<F> {
  dataTestId?: string;
  filterSets: ReadonlyArray<SavedFilterSet<F>>;
  onApply: (filters: F) => void;
  onDelete: (name: string) => void;
}

const BOOKMARK_ICON =
  "M12 18L7.8 19.8C7.13333 20.0833 6.5 20.0292 5.9 19.6375C5.3 19.2458 5 18.6917 5 17.975V5C5 4.45 5.19583 3.97917 5.5875 3.5875C5.97917 3.19583 6.45 3 7 3H17C17.55 3 18.0208 3.19583 18.4125 3.5875C18.8042 3.97917 19 4.45 19 5V17.975C19 18.6917 18.7 19.2458 18.1 19.6375C17.5 20.0292 16.8667 20.0833 16.2 19.8L12 18Z";

export default function SavedFilters<F>({
  dataTestId,
  filterSets,
  onApply,
  onDelete,
}: SavedFiltersProps<F>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const sortedFilterSets = [...filterSets].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label="Saved Filters"
        color="inherit"
        dataTestId={dataTestId}
        icon={<SvgIcon path={BOOKMARK_ICON} />}
        onClick={handleOpen}
        title="Saved Filters"
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        keepMounted
        MenuListProps={{
          disablePadding: true,
          onClick: (event) => {
            event.stopPropagation();
          },
        }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <Box width={260}>
          {filterSets.length === 0 ? (
            <Box p={1}>
              <Alert
                dataTestId={
                  dataTestId ? `${dataTestId}-empty-state` : undefined
                }
                dense
                severity="info"
              >
                <b>Saved</b> applied filter sets will display here.
              </Alert>
            </Box>
          ) : (
            <>
              <Typography
                component="div"
                fontWeight={600}
                px={2}
                py={1.5}
                variant="h6"
              >
                Saved Filters
              </Typography>
              {sortedFilterSets.map((filterSet) => (
                <MUIMenuItem
                  data-testid={
                    dataTestId
                      ? `${dataTestId}-option-${filterSet.name}`
                      : undefined
                  }
                  key={filterSet.name}
                  onClick={() => {
                    onApply(filterSet.filters);
                    handleClose();
                  }}
                >
                  <OverflowTooltipTypography flex={1}>
                    {filterSet.name}
                  </OverflowTooltipTypography>
                  <DeleteIconButton
                    dataTestId={
                      dataTestId
                        ? `${dataTestId}-delete-${filterSet.name}`
                        : undefined
                    }
                    iconSize="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDelete(filterSet.name);
                    }}
                    size="small"
                    title={`Delete ${filterSet.name}`}
                  />
                </MUIMenuItem>
              ))}
            </>
          )}
        </Box>
      </Menu>
    </>
  );
}
